import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { trackByRef } from 'src/app/lego-utils';
import { isEmpty } from 'lodash-es';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectItemInterface } from 'ng-zorro-antd/select';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { EntryCode, NotificationItem } from 'src/app/models';
import { NotificationDataService } from 'src/app/services';
import { EquipmentFacadeService } from 'src/app/services/equipment-facade.service';
import { ConstructionLogService, IPage, PagedCollection } from 'src/app/state';
import { CONSTRUCTION_LOG_PAGINATOR } from './construction-log-pagination.provider';

@Component({
  selector: 'em-construction-log',
  styleUrls: ['./construction-log.component.less'],
  templateUrl: './construction-log.component.html',
  standalone: false,
})
export class ConstructionLogComponent implements OnInit, OnDestroy {
  @ViewChild('longTextTemplate', { static: true })
  private readonly longTextTemplate!: TemplateRef<{ $implicit: string }>;

  public readonly trackByRef = trackByRef;
  public readonly error$ = this.constructionLogService.error$;
  public readonly loading$ = this.equipmentFacade.loading$;

  public perPage = 10;
  public pageIndex = 1;

  public readonly checkOptions = [
    { label: 'Construction log', value: 'ZF' },
    { label: 'Unplanned corr maint.', value: 'Z2' },
  ];
  public selectedEntryTypes = this.checkOptions.map(({ value }) => value);

  public readonly perPage$ = new BehaviorSubject<number>(this.perPage);
  public readonly selectedEntryTypes$ = new BehaviorSubject<string[]>([]);
  public readonly pagination$: Observable<IPage<NotificationItem>>;
  public readonly entryCodes$: Observable<EntryCode[]>;
  public readonly entryCodesControl = new UntypedFormControl([]);
  public areEntryCodesLoading = false;

  private readonly subscriptions = new Subscription();

  public constructor(
    @Inject(CONSTRUCTION_LOG_PAGINATOR)
    public readonly paginatorRef: PagedCollection<NotificationItem>,
    private readonly notificationDataService: NotificationDataService,
    private readonly constructionLogService: ConstructionLogService,
    private readonly modalService: NzModalService,
    private readonly equipmentFacade: EquipmentFacadeService
  ) {
    const equipmentNumber$ = this.equipmentFacade.selectedEquipmentId$.pipe(
      tap(() => {
        // Set loading to avoid showing "no data" until "this.paginatorRef.getPage(...)" is called
        this.paginatorRef.setLoading(true);

        // clear error on new equipment else the html will keep showing the error,
        // and never try to fetch the mould log for the new equipment
        this.constructionLogService.clearError();

        // entry codes technically start loading here
        this.areEntryCodesLoading = true;

        this.paginatorRef.setPage(1);
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    this.entryCodes$ = combineLatest([equipmentNumber$, this.selectedEntryTypes$]).pipe(
      switchMap(([equipmentNumber, selectedEntryTypes]) => {
        if (equipmentNumber === undefined) {
          return of([]);
        }

        return this.notificationDataService
          .getEntryCodesByEquipmentNumber(equipmentNumber, selectedEntryTypes)
          .pipe(catchError(() => of([])));
      }),
      tap((newEntryCodes) => {
        this.areEntryCodesLoading = false;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const selectedEntryCodes: EntryCode[] = this.entryCodesControl.value;

        const filteredEntryCodes = selectedEntryCodes.filter((entryCode) =>
          newEntryCodes.some((newEntryCode) => entryCode.code === newEntryCode.code)
        );

        // Remove codes no longer present in the list
        this.entryCodesControl.setValue(filteredEntryCodes);
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    const selectedEntryCodes$ = this.entryCodesControl.valueChanges.pipe(
      map((entryCodes: EntryCode[]) => {
        if (isEmpty(entryCodes)) {
          return undefined;
        }

        return entryCodes.map((entryCode) => entryCode.code);
      }),
      startWith(undefined)
    );

    const shouldClearCacheWhenChanged$ = combineLatest([
      equipmentNumber$,
      this.selectedEntryTypes$,
      selectedEntryCodes$,
      this.perPage$,
    ]).pipe(
      tap(() => {
        this.paginatorRef.clearCache();
      })
    );

    this.pagination$ = combineLatest([
      this.paginatorRef.pageChanges.pipe(
        tap((page) => {
          this.pageIndex = page;
        })
      ),
      shouldClearCacheWhenChanged$,
    ]).pipe(
      debounceTime(0), // wait for inputs to stabilize
      switchMap(([page, [equipmentNumber, selectedEntryTypes, selectedEntryCodes, perPage]]) => {
        if (equipmentNumber === undefined) {
          return this.paginatorRef.getPage(() =>
            of({
              currentPage: 1,
              data: [],
              lastPage: 1,
              perPage,
            })
          );
        }

        return this.paginatorRef.getPage(() =>
          this.constructionLogService.loadConstructionLog(
            { page, perPage },
            equipmentNumber,
            selectedEntryTypes,
            selectedEntryCodes
          )
        );
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  public showLongText(longText: string) {
    const modal = this.modalService.create({
      nzClosable: true,
      nzContent: this.longTextTemplate,
      nzData: longText,
      nzFooter: [
        {
          label: 'Close',
          onClick: () => {
            modal.destroy();
          },
        },
      ],
      nzMaskClosable: true,
      nzTitle: 'Long text',
      nzWidth: '80%',
    });
  }

  public ngOnInit(): void {
    this.updateSelected();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public pageSizeChange(perPage: number) {
    this.perPage$.next(perPage);
  }

  public updateSelected() {
    this.selectedEntryTypes$.next(this.selectedEntryTypes);
  }

  public entryCodeFilter(input: string, option: NzSelectItemInterface) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const value: EntryCode = option.nzValue;

    return `${value.code} - ${value.text}`.toLowerCase().includes(input.toLowerCase());
  }
}
